'use client';

import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import Link from 'next/link';
import styled, { css } from 'styled-components';
import { Icon } from '@ui/icons';
import { variants } from './variants';
import { StyledButton, IconContainer, HoverIcon } from './styles.js';

const isFullWidthMobile = ({ $fullWidth, $fullWidthMobile }) =>
  Boolean($fullWidth || $fullWidthMobile);

const isPercentWidthMobile = ({
  $fullWidth,
  $fullWidthMobile,
  $percentWidthMobile,
}) =>
  Boolean($percentWidthMobile) &&
  !isFullWidthMobile({ $fullWidth, $fullWidthMobile });

const isStretchedMobile = props =>
  isFullWidthMobile(props) || isPercentWidthMobile(props);

const isAlignedInParent = ({ $alignment }) =>
  $alignment === 'left' || $alignment === 'center' || $alignment === 'right';

const LinkRoot = styled.div`
  display: ${props => {
    if (isAlignedInParent(props)) return 'flex';
    return isStretchedMobile(props) ? 'block' : 'inline-block';
  }};
  justify-content: ${props => props.theme.getJustifyContent(props.$alignment)};
  max-width: 100%;
  width: ${props => {
    if (isFullWidthMobile(props) || isAlignedInParent(props)) return '100%';
    if (isPercentWidthMobile(props)) return `${props.$percentWidthMobile}%`;
    return 'auto';
  }};

  & > a {
    display: ${props => (isStretchedMobile(props) ? 'block' : 'inline')};
    width: ${props => (isStretchedMobile(props) ? '100%' : 'auto')};
  }

  /* Percent lives on the root (flex item); button fills that box. */
  ${props =>
    isPercentWidthMobile(props) &&
    css`
      & button {
        width: 100%;
      }
    `}

  @media ${props => props.theme.tablet} {
    display: ${props => {
      if (isAlignedInParent(props)) return 'flex';
      return props.$fullWidth || props.$fullWidthDesktop
        ? 'block'
        : 'inline-block';
    }};
    width: ${props =>
      props.$fullWidth || props.$fullWidthDesktop || isAlignedInParent(props)
        ? '100%'
        : 'auto'};

    & > a {
      display: ${props =>
        props.$fullWidth || props.$fullWidthDesktop ? 'block' : 'inline'};
      width: ${props =>
        props.$fullWidth || props.$fullWidthDesktop ? '100%' : 'auto'};
    }

    & button {
      width: ${props =>
        props.$fullWidth || props.$fullWidthDesktop ? '100%' : 'auto'};
    }
  }
`;

export const LinkButton = forwardRef(
  (
    {
      children,
      href,
      alignment,
      target,
      rel,
      horizontalPadding = 6,
      disabled,
      icon,
      onClick,
      verticalPadding = 0,
      percentWidthMobile,
      variant = 'primary',
      hoverIcon = null,
      minWidth = 0,
      fullWidth = false,
      fullWidthMobile = false,
      fullWidthDesktop = false,
      'data-testid': dataTestId,
    },
    ref,
  ) => {
    return (
      <LinkRoot
        $fullWidth={fullWidth}
        $fullWidthMobile={fullWidthMobile}
        $fullWidthDesktop={fullWidthDesktop}
        $percentWidthMobile={percentWidthMobile}
        $alignment={alignment}
      >
        <Link href={href} target={target} rel={rel}>
          <StyledButton
            ref={ref}
            as="button"
            type="button"
            disabled={disabled}
            onClick={onClick}
            $horizontalPadding={horizontalPadding}
            $verticalPadding={verticalPadding}
            $percentWidthMobile={percentWidthMobile}
            $variant={variant}
            $hasHoverIcon={!!hoverIcon}
            $minWidth={minWidth}
            $fullWidth={fullWidth}
            $fullWidthMobile={fullWidthMobile}
            $fullWidthDesktop={fullWidthDesktop}
            $icon={icon?.position}
            data-testid={dataTestId}
          >
            {icon?.position === 'before' && (
              <IconContainer aria-hidden="true" position={icon.position}>
                <Icon {...icon} id="link_button_before" />
              </IconContainer>
            )}
            {children}
            {icon?.position === 'after' ||
              (icon?.position === 'end' && (
                <IconContainer aria-hidden="true" position={icon.position}>
                  <Icon {...icon} id="link_button_after" />
                </IconContainer>
              ))}
            {hoverIcon && (
              <HoverIcon aria-hidden="true">
                <Icon
                  alt=""
                  size="22px"
                  iconName={hoverIcon}
                  color={variants[variant]?.active.text}
                  id="link_button_hover"
                />
              </HoverIcon>
            )}
          </StyledButton>
        </Link>
      </LinkRoot>
    );
  },
);

LinkButton.displayName = 'LinkButton';

LinkButton.propTypes = {
  children: PropTypes.node.isRequired,
  horizontalPadding: PropTypes.number,
  verticalPadding: PropTypes.number,
  href: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  alignment: PropTypes.oneOf(['left', 'center', 'right']),
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  icon: PropTypes.shape({
    position: PropTypes.oneOf(['before', 'after', 'end']),
  }),
  percentWidthMobile: PropTypes.number,
  hoverIcon: PropTypes.string,
  minWidth: PropTypes.number,
  fullWidth: PropTypes.bool,
  fullWidthMobile: PropTypes.bool,
  fullWidthDesktop: PropTypes.bool,
  target: PropTypes.string,
  rel: PropTypes.string,
  variant: PropTypes.oneOf(Object.keys(variants)),
  'data-testid': PropTypes.string,
};
